/**
 * AdminHustleEventsPage — admin builder for HustleEvents.
 *
 * Lets an admin create a new event, choose the seating model
 * (general / seated / tables), lay out sections × rows × seats OR
 * banquet tables, publish or cancel it, and see the seat/ticket
 * manifest.
 *
 * Talks to:
 *   POST  /api/admin/hustle-events
 *   POST  /api/admin/hustle-events/{id}/publish
 *   POST  /api/admin/hustle-events/{id}/cancel
 *   GET   /api/admin/hustle-events/{id}/manifest
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Plus, X, LayoutGrid, Users, Ticket } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const authHeader = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function Section({ sec, onChange, onRemove }) {
  return (
    <div className="rounded-xl border border-stone-200 dark:border-stone-800 p-4 space-y-2">
      <div className="flex items-center justify-between">
        <p className="font-bold text-sm">Section {sec.code || "—"}</p>
        <button type="button" onClick={onRemove}
          data-testid={`remove-section-${sec.code || sec._id}`}
          className="text-stone-400 hover:text-red-500">
          <X size={16} />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input
          className="h-9 px-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent text-sm"
          placeholder="Code (e.g. A)"
          value={sec.code || ""}
          onChange={(e) => onChange({ ...sec, code: e.target.value.toUpperCase().slice(0, 4) })}
          data-testid={`sec-code-${sec._id}`}
        />
        <input
          className="h-9 px-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent text-sm"
          placeholder="Label"
          value={sec.label || ""}
          onChange={(e) => onChange({ ...sec, label: e.target.value })}
          data-testid={`sec-label-${sec._id}`}
        />
        <input
          className="h-9 px-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent text-sm"
          placeholder="Rows (comma sep, e.g. 1,2,3)"
          value={(sec.rows || []).join(",")}
          onChange={(e) => onChange({ ...sec, rows: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
          data-testid={`sec-rows-${sec._id}`}
        />
        <input
          type="number"
          className="h-9 px-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent text-sm"
          placeholder="Seats/row"
          value={sec.seats_per_row || ""}
          onChange={(e) => onChange({ ...sec, seats_per_row: parseInt(e.target.value || "0", 10) })}
          data-testid={`sec-spr-${sec._id}`}
        />
        <input
          type="number"
          className="h-9 px-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent text-sm"
          placeholder="Price (cents)"
          value={sec.price_cents || ""}
          onChange={(e) => onChange({ ...sec, price_cents: parseInt(e.target.value || "0", 10) })}
          data-testid={`sec-price-${sec._id}`}
        />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox"
            checked={!!sec.is_vip}
            onChange={(e) => onChange({ ...sec, is_vip: e.target.checked })}
            data-testid={`sec-vip-${sec._id}`}
          />
          VIP
        </label>
      </div>
    </div>
  );
}

function Table({ tb, onChange, onRemove }) {
  return (
    <div className="rounded-xl border border-stone-200 dark:border-stone-800 p-4 space-y-2">
      <div className="flex items-center justify-between">
        <p className="font-bold text-sm">Table {tb.code || "—"}</p>
        <button type="button" onClick={onRemove}
          data-testid={`remove-table-${tb.code || tb._id}`}
          className="text-stone-400 hover:text-red-500">
          <X size={16} />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input
          className="h-9 px-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent text-sm"
          placeholder="Code (e.g. T1)"
          value={tb.code || ""}
          onChange={(e) => onChange({ ...tb, code: e.target.value.toUpperCase().slice(0, 4) })}
          data-testid={`tb-code-${tb._id}`}
        />
        <input
          className="h-9 px-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent text-sm"
          placeholder="Label"
          value={tb.label || ""}
          onChange={(e) => onChange({ ...tb, label: e.target.value })}
          data-testid={`tb-label-${tb._id}`}
        />
        <input
          type="number"
          className="h-9 px-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent text-sm"
          placeholder="Capacity"
          value={tb.capacity || ""}
          onChange={(e) => onChange({ ...tb, capacity: parseInt(e.target.value || "0", 10) })}
          data-testid={`tb-capacity-${tb._id}`}
        />
        <input
          type="number"
          className="h-9 px-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent text-sm"
          placeholder="Price/seat (cents)"
          value={tb.price_per_seat_cents || ""}
          onChange={(e) => onChange({ ...tb, price_per_seat_cents: parseInt(e.target.value || "0", 10) })}
          data-testid={`tb-price-${tb._id}`}
        />
        <input
          className="h-9 px-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent text-sm col-span-2"
          placeholder="Menu id (optional)"
          value={tb.menu_id || ""}
          onChange={(e) => onChange({ ...tb, menu_id: e.target.value })}
          data-testid={`tb-menu-${tb._id}`}
        />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox"
            checked={!!tb.is_vip}
            onChange={(e) => onChange({ ...tb, is_vip: e.target.checked })}
            data-testid={`tb-vip-${tb._id}`}
          />
          VIP
        </label>
      </div>
    </div>
  );
}

let uid = 0;

export default function AdminHustleEventsPage() {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [venueName, setVenueName] = useState("");
  const [venueAddr, setVenueAddr] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [duration, setDuration] = useState(180);
  const [mode, setMode] = useState("general");
  const [gaCap, setGaCap] = useState(100);
  const [gaPrice, setGaPrice] = useState(0);
  const [sections, setSections] = useState([]);
  const [tables, setTables] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    document.title = "HustleEvents · Admin builder";
  }, []);

  const addSection = () =>
    setSections((s) => [...s, {
      _id: ++uid, code: "", label: "", rows: [], seats_per_row: 10,
      price_cents: 0, is_vip: false,
    }]);
  const addTable = () =>
    setTables((tb) => [...tb, {
      _id: ++uid, code: "", label: "", capacity: 10,
      price_per_seat_cents: 0, is_vip: false, menu_id: "",
    }]);

  const save = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      const payload = {
        title, description, venue_name: venueName, venue_address: venueAddr,
        starts_at: startsAt ? new Date(startsAt).toISOString() : null,
        duration_min: duration, seating_mode: mode,
        sections: mode === "seated" ? sections.map(({ _id, ...s }) => s) : [],
        tables: mode === "tables" ? tables.map(({ _id, ...s }) => s) : [],
        general_capacity: mode === "general" ? gaCap : 0,
        general_price_cents: mode === "general" ? gaPrice : 0,
      };
      const { data } = await axios.post(`${API}/admin/hustle-events`,
        payload, { headers: authHeader(), withCredentials: true });
      setSaved(data);
      toast.success("Event draft created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!saved) return;
    try {
      await axios.post(`${API}/admin/hustle-events/${saved.id}/publish`,
        {}, { headers: authHeader(), withCredentials: true });
      toast.success("Event published!");
      setSaved({ ...saved, status: "published" });
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  return (
    <div
      data-testid="admin-hustle-events-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100"
    >
      <PlatformHeader brand={{ name: "HustleEvents · Admin", to: "/hustle-events" }}
        showBookCta={false} showCart={false} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        <h1 className="text-3xl md:text-4xl font-black">
          {t("events_admin_title", { defaultValue: "Create a HustleEvent" })}
        </h1>
        <p className="mt-2 text-stone-500">
          {t("events_admin_sub", {
            defaultValue: "Draft an event, add seats or tables, then publish when it's live.",
          })}
        </p>

        <div className="mt-8 space-y-4">
          <input
            className="w-full h-11 px-4 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent"
            placeholder="Event title"
            data-testid="admin-event-title"
            value={title} onChange={(e) => setTitle(e.target.value)}
          />
          <textarea
            className="w-full h-24 px-4 py-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent"
            placeholder="Description"
            data-testid="admin-event-desc"
            value={description} onChange={(e) => setDescription(e.target.value)}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              className="h-11 px-4 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent"
              placeholder="Venue name"
              data-testid="admin-event-venue-name"
              value={venueName} onChange={(e) => setVenueName(e.target.value)}
            />
            <input
              className="h-11 px-4 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent"
              placeholder="Venue address"
              data-testid="admin-event-venue-addr"
              value={venueAddr} onChange={(e) => setVenueAddr(e.target.value)}
            />
            <input
              type="datetime-local"
              className="h-11 px-4 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent"
              data-testid="admin-event-startsat"
              value={startsAt} onChange={(e) => setStartsAt(e.target.value)}
            />
            <input
              type="number"
              className="h-11 px-4 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent"
              placeholder="Duration (min)"
              data-testid="admin-event-duration"
              value={duration} onChange={(e) => setDuration(parseInt(e.target.value || "0", 10))}
            />
          </div>

          <div className="flex flex-wrap gap-2 mt-2">
            {[
              ["general", Ticket, "General admission"],
              ["seated",  LayoutGrid, "Sections & rows"],
              ["tables",  Users, "Round tables"],
            ].map(([k, Icon, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setMode(k)}
                data-testid={`admin-mode-${k}`}
                className={`inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-bold transition ${
                  mode === k
                    ? "bg-amber-400 text-slate-950"
                    : "bg-stone-200 dark:bg-stone-800 text-stone-500"
                }`}
              >
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>

          {mode === "general" && (
            <div className="grid grid-cols-2 gap-4">
              <input
                type="number"
                className="h-11 px-4 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent"
                placeholder="Capacity"
                data-testid="admin-ga-capacity"
                value={gaCap}
                onChange={(e) => setGaCap(parseInt(e.target.value || "0", 10))}
              />
              <input
                type="number"
                className="h-11 px-4 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent"
                placeholder="Price (cents)"
                data-testid="admin-ga-price"
                value={gaPrice}
                onChange={(e) => setGaPrice(parseInt(e.target.value || "0", 10))}
              />
            </div>
          )}

          {mode === "seated" && (
            <div className="space-y-3">
              {sections.map((sec) => (
                <Section
                  key={sec._id}
                  sec={sec}
                  onChange={(s) => setSections((prev) => prev.map((x) => x._id === sec._id ? s : x))}
                  onRemove={() => setSections((prev) => prev.filter((x) => x._id !== sec._id))}
                />
              ))}
              <Button
                type="button" onClick={addSection}
                data-testid="admin-add-section"
                className="bg-stone-200 dark:bg-stone-800 text-stone-800 dark:text-stone-100 hover:bg-amber-300"
              >
                <Plus size={16} /> Add section
              </Button>
            </div>
          )}

          {mode === "tables" && (
            <div className="space-y-3">
              {tables.map((tb) => (
                <Table
                  key={tb._id}
                  tb={tb}
                  onChange={(x) => setTables((prev) => prev.map((y) => y._id === tb._id ? x : y))}
                  onRemove={() => setTables((prev) => prev.filter((y) => y._id !== tb._id))}
                />
              ))}
              <Button
                type="button" onClick={addTable}
                data-testid="admin-add-table"
                className="bg-stone-200 dark:bg-stone-800 text-stone-800 dark:text-stone-100 hover:bg-amber-300"
              >
                <Plus size={16} /> Add table
              </Button>
            </div>
          )}

          <div className="flex flex-wrap gap-3 pt-6 border-t border-stone-200 dark:border-stone-800">
            <Button
              onClick={save} disabled={saving}
              data-testid="admin-save-event"
              className="bg-amber-400 hover:bg-amber-300 text-slate-950 font-black rounded-full h-11 px-6"
            >
              {saving ? "Saving…" : "Save draft"}
            </Button>
            {saved?.status === "draft" && (
              <Button
                onClick={publish}
                data-testid="admin-publish-event"
                className="bg-emerald-500 hover:bg-emerald-400 text-white font-black rounded-full h-11 px-6"
              >
                Publish
              </Button>
            )}
          </div>

          {saved && (
            <div className="mt-6 rounded-xl border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 dark:border-emerald-800 p-4 text-sm">
              <p><strong>ID:</strong> <code>{saved.id}</code></p>
              <p><strong>Slug:</strong> <code>{saved.slug}</code></p>
              <p><strong>Status:</strong> {saved.status}</p>
              <p className="mt-2">
                <a
                  href={`/hustle-events/${saved.slug}`}
                  className="text-amber-600 font-bold hover:underline"
                  data-testid="admin-view-live-event"
                >
                  View live →
                </a>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
